/* Copyright CloudLinux Zug GmbH 2010-2017 All Rights Reserved
 *
 * Licensed under CLOUD LINUX LICENSE AGREEMENT
 * http://cloudlinux.com/docs/LICENSE.TXT
 */

import { Injectable } from '@angular/core';
import { XHR } from '../utils/xhr';
import { plainToClass } from 'class-transformer';
import { Application, ApplicationCommand, NodeJSInformation,
    SelectorStatus } from '../nodejs/model';
import { Helper } from '../utils/helper';

@Injectable()
export class SelectorRepo {

    public command = 'cloudlinux-selector';

    constructor(private request: XHR) {}

    getSelectorStatus() {
        return this.request.post({
            command: this.command,
            method: 'get',
            params: {'get-selector-status': '', 'interpreter': 'php'},
        });
    }

    setSelectorStatus(value) {
        return this.request.post({
            command: this.command,
            method: 'set',
            params: {'selector-status': value, 'interpreter': 'php'},
        });
    }

    getSelectorInfo() {
        return this.request.post({
            command: this.command,
            method: 'get',
        });
    }

    setDefaultVersion(value) {
        return this.request.post({
            command: this.command,
            method: 'set',
            params: {'default-version': value, 'interpreter': 'php'},
        });
    }

    setSupportedPhpVersions(value) {
        return this.request.post({
            command: this.command,
            method: 'set',
            params: {'supported-versions': JSON.stringify(value), 'interpreter': 'php'},
        });
    }

    setExtensions(extensions, version) {
        let extension_new = {};
        for (let name in extensions) {
          if (extensions.hasOwnProperty(name)) {
            if (!extensions[name].disable) {
              extension_new[name] = (extensions[name].state) ? 'enabled' : 'disabled';
            }
          }
        }
        return this.request.post({
            command: this.command,
            method: 'set',
            params: {
                'extensions': JSON.stringify(extension_new),
                'version': version,
                'interpreter': 'php'
            },
        });
    }

    /*
     * ==============================================
     * ================= NodeJS API =================
     * ==============================================
     */

    public async getNodeJsInformation(convertToClass = true): Promise<NodeJSInformation> {
        let result = await this.request.post({
            command: this.command,
            method: 'get',
            params: {'interpreter': 'nodejs'}
        }, 'cloudlinux-selector-get-nodejs'); // TODO: remove mocking
        if (convertToClass) {
            result = plainToClass(NodeJSInformation, result);
        }
        return result;
    }

    public async removeNodeJsVersion(version: string): Promise<Object> {
        return await this.request.post({
            command: this.command,
            method: 'uninstall-version',
            params: {
                'interpreter': 'nodejs',
                'version': version
            }
        }, 'cloudlinux-selector-remove-nodejs-version'); // TODO: remove mocking
    }

    public async installNodeJsVersion(version: string): Promise<Object> {
        return await this.request.post({
            command: this.command,
            method: 'install-version',
            params: {
                'interpreter': 'nodejs',
                'version': version
            }
        }, 'cloudlinux-selector-install-nodejs-version'); // TODO: remove mocking
    }

    /**
     * Enable or disable NodeJS installed version.
     *
     * @param {string} version - Major version of NodeJS
     * @param {boolean} enabled
     * @returns {Promise<Object>}
     */
    public async switchStatusOfNodeJsVersion(version: string, enabled: boolean): Promise<Object> {
        return await this.request.post({
            command: this.command,
            method: ((enabled) ? 'enable' : 'disable')  + '-version',
            params: {
                'version': version,
                'interpreter': 'nodejs'
            }
        }, 'cloudlinux-selector-switch-nodejs-version'); // TODO: remove mocking
    }

    /**
     * Make default NodeJS certain Version
     *
     * @param {string} version - Major version of NodeJS
     * @returns {Promise<Object>}
     */
    public async setDefaultNodeJsVersion(version: string): Promise<Object> {
        return await this.request.post({
            command: this.command,
            method: 'set',
            params: {
                'interpreter': 'nodejs',
                'default-version': version
            }
        }, 'cloudlinux-selector-make-default-version'); // TODO: remove mocking
    }

    public async changeNodeJsStatus(status: SelectorStatus): Promise<Object> {
        return await this.request.post({
            command: this.command,
            method: 'set',
            params: {
                'interpreter': 'nodejs',
                'selector-status': status
            }
        }, 'cloudlinux-selector-selector-status'); // TODO: remove mocking
    }

    public async changeAppVersion(appName: string, user: string, version: string): Promise<Object> {
        return await this.request.post({
            command: this.command,
            method: 'set',
            params: {
                'interpreter': 'nodejs',
                'user': user,
                'app-root': appName,
                'new-version': version
            }
        }, 'cloudlinux-selector-new-version'); // TODO: remove mocking
    }

    public async moveNodeJsVersion(version: string, newVersion: string): Promise<Object> {
        return await this.request.post({
            command: this.command,
            method: 'set',
            params: {
                'interpreter': 'nodejs',
                'from-version': version,
                'new-version': newVersion,

            }
        }, 'cloudlinux-selector-move-nodejs-version'); // TODO: remove mocking
    }

    public async getListOfApplicationsByUser(userName: string, convertToClass = true): Promise<NodeJSInformation> {
        let result = await this.request.post({
            command: this.command,
            method: 'get',
            params: {
                'interpreter': 'nodejs',
                'user': userName,
            }
        });
        if (convertToClass) {
            result = plainToClass(NodeJSInformation, result);
        }
        return result;
    }

    public async sendApplicationCommand(
        appCommand: ApplicationCommand, appName: string, version = '',
    ): Promise<Object> {
        let params = {
            'interpreter': 'nodejs',
            'app-root': appName,
        };
        if (version) params['version'] = version;
        return await this.request.post({
            command: this.command,
            method: appCommand,
            params: params
        });
    }

    public async saveCommand(
        app: Application,
        originApp: Application, userName: string = ''
    ): Promise<Object> {
        let params = {
            'interpreter': 'nodejs',
            'app-root': originApp.appName,
            'env-vars': JSON.stringify(app.env_vars)
        };

        if (originApp.appName !== app.appName) params['new-app-root'] = app.appName;

        let dynamicParams = {
            'app-mode': 'app_mode',
            'new-domain': 'domain',
            'new-app-uri': 'app_uri',
            'new-version': 'majorNodeJSVersion',
        };

        for (let param in dynamicParams) {
            let appParam = dynamicParams[param];
            if (app[appParam] !== originApp[appParam]) {
                params[param] = app[appParam];
            }
        }
        if (app['startup_file'] !== originApp['startup_file'] && app['startup_file'] !== '') {
            params['startup-file'] = app['startup_file'];
        }
        if (params['new-domain']) {
            params['new-app-uri'] = app.app_uri;
        }
        if (params['new-app-uri'] === '') {
            params['new-app-uri'] = '/';
        }

        if (userName) params['user'] = userName;
        return await this.request.post({
            command: this.command,
            method: 'set',
            params: params
        });
    }

    public async createCommand(app: Application, userName: string = ''
    ): Promise<Object> {
        let params = {
            'interpreter': 'nodejs',
            'app-root': app.appName,
            'app-uri': app.app_uri ? app.app_uri : "/",
            'app-mode': app.app_mode,
            'version': app.majorNodeJSVersion,
            'startup-file': app.startup_file,
            'env-vars': JSON.stringify(app.env_vars)
        };

        for (let param in params) {
            if (params[param] === '') delete params[param];
        }

        if (userName) {
            params['user'] = userName;
        } else if (app.domain) {
            params['domain'] = app.domain;
        }
        return await this.request.post({
            command: this.command,
            method: 'create',
            params: params
        });
    }

    /**
     * Should cover cloudlinux-selector read-config ...
     * @param {string} appName
     * @param {string} configFile
     * @param {string} userName
     * @returns {Promise<string>}
     */
    public async readConfig(
        appName: string,
        configFile: string,
        userName: string = null
    ): Promise<string> {
        let _params = {
            'interpreter': 'nodejs',
            'app-root': appName,
            'config-file': configFile
        };

        // Do not need user!
        let response = await this.request.post({
            command: this.command,
            method: 'read-config',
            params: _params
        });
        return Helper.unicodeBase64ToString(response.data);
    }

    /**
     * Should cover cloudlinux-selector run-script ...
     * @param {string} appName
     * @param {string} scriptName
     * @param params
     * @returns {Promise<Object>}
     */
    public async runScript(
        appName: string,
        scriptName: string,
        params?: any
    ): Promise<string> {
        let _params = {
            'interpreter': 'nodejs',
            'app-root': appName,
            'script-name': scriptName
        };
        if (params) {
            _params['additional-params'] = params;
        }
        let response = await this.request.post({
            command: this.command,
            method: 'run-script',
            params: _params
        });
        return Helper.unicodeBase64ToString(response.data);
    }

    /**
     * Should cover cloudlinux-selector read-config ...
     * @param {string} appName
     * @param {string} fileName
     * @param {string} content
     * @param {string} userName
     * @returns {Promise<string>}
     */
    public async saveConfig(appName: string, fileName: string,
                            content: string, userName: string = null): Promise<string> {
        let params = {
            'interpreter': 'nodejs',
            'app-root': appName,
            'config-file': fileName,
            'content': btoa(content)
        };
        if (userName) {
            params['user'] = userName;
        }
        return await this.request.post({
            command: this.command,
            method: 'save-config',
            params: params
        });
    }

    public async cldiag(): Promise<any> {
        return await this.request.post({
            'command': 'cldiag'
        });
    }

}



// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./node_modules/angular-router-loader/src?loader=system&genDir=compiled&aot=false!./src/app/repository/selector_repo.ts